import { CardType, ControlType, type FilterCardConfig } from '../models/card-model';
import type { CardList } from '../models/layout.model';
import {
	ChartCardConfig,
	parseGranularity,
	type TimeDimensionConfig,
} from '../models/chart-config.model';
import type { TableCardConfig } from '../models/table-config.model';
import type { DateRangeExpression } from '../models/date-range-expression.model';
import {
	coerceToDateRangeExpression,
	DEFAULT_WEEK_START,
	expressionFromResolvedDates,
	formatDateToISO,
	parseISODate,
	resolveDateRangeExpression,
	type WeekStartOptions,
} from './date-range.util';
import { resolveFilterControlType } from './filter-control-type.util';
import { coalesceLineChartX } from './highcharts-datetime.util';
import { parseSeriesDataPoints } from './series-data-point.util';

type ChartStoredDateRange = DateRangeExpression;

export type ResolvedTimeDimensionSource = 'global' | 'chart';

/** True for a single layout tile that renders the date-range filter control. */
export function isDateRangeFilterCardItem(
	layoutItem: Pick<CardList, 'cardType' | 'controlType' | 'cardId'> | undefined,
	config?: Pick<FilterCardConfig, 'controlType'>
): boolean {
	if (layoutItem?.cardType !== CardType.Filter) {
		return false;
	}
	try {
		return (
			resolveFilterControlType({
				layout: layoutItem,
				config: config
					? { ...config, cardId: layoutItem.cardId }
					: undefined,
			}) === ControlType.DateRange
		);
	} catch {
		// Legacy filter tiles without controlType render as date-range filters.
		return true;
	}
}

/** True when the page layout includes a date-range filter card (not dropdown-only). */
export function pageHasDateRangeFilterCard(
	pageLayout: readonly CardList[],
	getCardConfig?: (
		cardId: string
	) => Pick<FilterCardConfig, 'controlType'> | undefined
): boolean {
	return pageLayout.some(item =>
		isDateRangeFilterCardItem(item, getCardConfig?.(item.cardId))
	);
}

/**
 * Page-level range for global-sourced chart/table cards.
 * Without a date-range filter card → report settings only.
 * With a filter card → session picker override, else report settings.
 */
export function resolvePageGlobalDateRangeExpression(
	pageLayout: readonly CardList[],
	sessionFilterExpression: DateRangeExpression | undefined,
	reportDefaultExpression: DateRangeExpression,
	getCardConfig?: (
		cardId: string
	) => Pick<FilterCardConfig, 'controlType'> | undefined
): DateRangeExpression {
	if (!pageHasDateRangeFilterCard(pageLayout, getCardConfig)) {
		return reportDefaultExpression;
	}
	return sessionFilterExpression ?? reportDefaultExpression;
}

/** Normalizes `timeDimension.timeDimensionSource` from card config. */
export function resolveTimeDimensionSource(
	primary: Record<string, unknown> | undefined
): ResolvedTimeDimensionSource | undefined {
	if (!primary) {
		return undefined;
	}
	const raw = primary['timeDimensionSource'];
	if (typeof raw !== 'string') {
		return undefined;
	}
	const normalized = raw.trim().toLowerCase();
	if (normalized === 'global' || normalized === 'chart') {
		return normalized;
	}
	return undefined;
}

/** True when the card explicitly uses the page/report date range (`timeDimensionSource: global`). */
export function cardUsesGlobalTimeDimensionSource(
	cfg: CardWithTimeDimensionData | { data?: unknown }
): boolean {
	const primary = getPrimaryChartTimeDimension(
		cfg as CardWithTimeDimensionData
	);
	return resolveTimeDimensionSource(primary) === 'global';
}

/** True when no date range is stored (null, undefined, or empty array from API). */
function isUnsetDateRange(raw: unknown): boolean {
	return (
		raw === null ||
		raw === undefined ||
		(Array.isArray(raw) && raw.length === 0)
	);
}

/** Chart and table cards store preview date range on `data.timeDimension(s)`. */
export type CardWithTimeDimensionData =
	| Pick<ChartCardConfig, 'data'>
	| Pick<TableCardConfig, 'data'>;

export function getPrimaryChartTimeDimension(
	cfg: CardWithTimeDimensionData
): Record<string, unknown> | undefined {
	const data = cfg.data as Record<string, unknown> | undefined;
	if (!data) return undefined;
	const arr = data['timeDimensions'] as unknown[] | undefined;
	if (arr?.length) return arr[0] as Record<string, unknown>;
	const single = data['timeDimension'];
	if (single && typeof single === 'object') {
		return single as Record<string, unknown>;
	}
	return undefined;
}

/** Whether the card has a usable date axis (`timeDimension` or `timeDimensions[0]`). */
export function chartHasTimeDimension(cfg: ChartCardConfig): boolean {
	const primary = getPrimaryChartTimeDimension(cfg);
	if (!primary) {
		return false;
	}
	const dimension = primary['dimension'];
	if (typeof dimension === 'string' && dimension.trim().length > 0) {
		return true;
	}
	const granularity = primary['granularity'];
	return (
		granularity !== undefined &&
		granularity !== null &&
		String(granularity).trim().length > 0
	);
}

/** Maps `timeDimension` or `timeDimensions[0]` onto the typed card config field. */
export function timeDimensionConfigFromPrimary(
	primary: Record<string, unknown>
): TimeDimensionConfig {
	const config: TimeDimensionConfig = {
		dimension:
			typeof primary['dimension'] === 'string'
				? primary['dimension']
				: 'date',
		granularity: parseGranularity(primary['granularity']),
		timeDimensionSource:
			typeof primary['timeDimensionSource'] === 'string'
				? primary['timeDimensionSource']
				: 'chart',
	};

	const dateRange = primary['dateRange'];
	if (!isUnsetDateRange(dateRange)) {
		config.dateRange = [...coerceToDateRangeExpression(dateRange)];
	}

	return config;
}

function normalizeChartDateRange(
	raw: unknown
): ChartStoredDateRange | undefined {
	if (isUnsetDateRange(raw)) {
		return undefined;
	}
	return [...coerceToDateRangeExpression(raw)] as ChartStoredDateRange;
}

function primaryChartStoredPresetId(cfg: ChartCardConfig): string | undefined {
	const expr = chartTimeDimensionToExpression(cfg);
	if (expr?.length === 1) {
		return expr[0];
	}
	return undefined;
}

/**
 * Maps chart time dimension date range to a {@link DateRangeExpression} for the picker.
 */
export function chartTimeDimensionToExpression(
	cfg: ChartCardConfig
): DateRangeExpression | undefined {
	const td = getPrimaryChartTimeDimension(cfg);
	const dr = td ? normalizeChartDateRange(td['dateRange']) : undefined;
	return dr;
}

/**
 * Default preset id for the hidden date-range picker when the card stores a preset range.
 */
function chartPreviewDateRangeSelectedPresetId(cfg: ChartCardConfig): string {
	const expr = chartTimeDimensionToExpression(cfg);
	if (expr?.length === 1) {
		return expr[0];
	}
	return primaryChartStoredPresetId(cfg) ?? 'last30days';
}

/** Week presets require `weekStart` when resolving for `getCardData` / card preview (JEPTO-1SM). */
export const cardDataDateRangeResolveOptions = {
	weekStart: DEFAULT_WEEK_START,
};

/**
 * Builds `TimeDimensionsInput` for **`getCardData`** — resolves expression to ISO tuple.
 */
export function buildGetCardDataTimeDimensionInput(
	cfg: CardWithTimeDimensionData,
	expression: DateRangeExpression
): Record<string, unknown> {
	const resolved = resolveDateRangeExpression(
		expression,
		cardDataDateRangeResolveOptions
	);
	const start = formatDateToISO(resolved.startDate);
	const end = formatDateToISO(resolved.endDate);
	const prev = getPrimaryChartTimeDimension(cfg);

	return {
		granularity: parseGranularity(prev?.['granularity']),
		dateRange: [start, end],
	};
}

/**
 * When the view/page global date range changes, chart/table cards that follow that range
 * should refetch `cardData`. Cards with `timeDimensionSource: 'chart'` and a stored
 * expression should not be overwritten by the global picker.
 */
export function shouldRefetchCardDataOnGlobalDateChange(
	card: { cardType: CardType; data?: unknown } | undefined
): boolean {
	if (!card) {
		return true;
	}
	if (card.cardType !== CardType.Chart && card.cardType !== CardType.Table) {
		return true;
	}
	if (cardUsesGlobalTimeDimensionSource(card)) {
		return true;
	}
	const primary = getPrimaryChartTimeDimension(
		card as CardWithTimeDimensionData
	);
	if (!primary) {
		return true;
	}
	const src = resolveTimeDimensionSource(primary);
	// Use Default OFF persists `timeDimensionSource: 'chart'` — never follow page/filter range.
	if (src === 'chart') {
		return false;
	}
	return true;
}

/**
 * Preview toolbar + `getCardData` expression. When Use Default is off, only the card's
 * stored range applies — not transient preview overrides or the page filter card.
 */
export function resolveCardEditorPreviewDateRangePickerExpression(
	cfg: ChartCardConfig,
	previewOverride: DateRangeExpression | undefined,
	reportDefaultDateRange?: DateRangeExpression
): DateRangeExpression {
	if (!chartUsesDefaultDateRange(cfg)) {
		return (
			chartTimeDimensionToExpression(cfg) ??
			resolveChartEditorPreviewDateRangeExpression(
				cfg,
				reportDefaultDateRange
			)
		);
	}
	return (
		previewOverride ??
		resolveChartEditorPreviewDateRangeExpression(
			cfg,
			reportDefaultDateRange
		)
	);
}

/** Builds expression from legacy ISO tuple when reading old stored data is not supported. */
export function defaultLast30DaysExpression(): DateRangeExpression {
	return ['last30days'];
}

/** Report default for card editor UI when `getView.dateRange` is absent. */
export function resolveCardEditorReportDefaultDateRangeExpression(
	viewDateRange?: DateRangeExpression
): DateRangeExpression {
	return viewDateRange ?? defaultLast30DaysExpression();
}

export function cardEditorReportDefaultDateRangeSelectedPresetId(
	viewDateRange?: DateRangeExpression
): string {
	const expr =
		resolveCardEditorReportDefaultDateRangeExpression(viewDateRange);
	if (expr.length === 1) {
		return expr[0];
	}
	return 'last30days';
}

/** Matches Data tab "Use Default Date Range" toggle from persisted `timeDimension`. */
export function chartUsesDefaultDateRange(cfg: ChartCardConfig): boolean {
	if (cardUsesGlobalTimeDimensionSource(cfg)) {
		return true;
	}
	const td = getPrimaryChartTimeDimension(cfg);
	if (!td) {
		return true;
	}
	const src = resolveTimeDimensionSource(td);
	if (src === 'chart') {
		return false;
	}
	const range = td['dateRange'];
	const hasTuple =
		Array.isArray(range) &&
		range.length === 2 &&
		typeof range[0] === 'string' &&
		typeof range[1] === 'string';
	return !hasTuple;
}

/**
 * Initial preview toolbar range — aligned with Data tab `dataTabDateRangeDisplayExpression`.
 * Global/default cards use `getView.dateRange` when present, else Last 30 days.
 */
export function resolveChartEditorPreviewDateRangeExpression(
	cfg: ChartCardConfig,
	viewDateRange?: DateRangeExpression
): DateRangeExpression {
	if (chartUsesDefaultDateRange(cfg)) {
		return resolveCardEditorReportDefaultDateRangeExpression(viewDateRange);
	}
	return (
		chartTimeDimensionToExpression(cfg) ??
		resolveCardEditorReportDefaultDateRangeExpression(viewDateRange)
	);
}

/** Preset id for preview date picker when the card follows the report default range. */
export function chartEditorPreviewDateRangeSelectedPresetId(
	cfg: ChartCardConfig,
	viewDateRange?: DateRangeExpression
): string {
	if (chartUsesDefaultDateRange(cfg)) {
		return cardEditorReportDefaultDateRangeSelectedPresetId(viewDateRange);
	}
	return chartPreviewDateRangeSelectedPresetId(cfg);
}

/** If stored range is only ISO strings, convert to expression (fixed pair). */
export function expressionFromIsoTupleIfNeeded(
	start: string,
	end: string,
	options?: WeekStartOptions
): DateRangeExpression {
	return expressionFromResolvedDates(parseISODate(start), parseISODate(end), {
		weekStart: options?.weekStart ?? DEFAULT_WEEK_START,
	});
}

const MS_PER_DAY = 86_400_000;
const GLOBAL_RANGE_START_SLACK_DAYS = 31;

function seriesPointXToDate(x: string | number): Date | null {
	const coalesced = coalesceLineChartX(x);
	if (typeof coalesced === 'number' && Number.isFinite(coalesced)) {
		const d = new Date(coalesced);
		return Number.isNaN(d.getTime()) ? null : d;
	}
	if (typeof coalesced === 'string') {
		const trimmed = coalesced.trim();
		if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
			try {
				return parseISODate(trimmed.slice(0, 10));
			} catch {
				return null;
			}
		}
		const d = new Date(trimmed);
		return Number.isNaN(d.getTime()) ? null : d;
	}
	return null;
}

/** Min/max dates inferred from chart WebSocket `series[].data` points. */
function inferDateSpanFromChartWebSocketData(
	wsData: unknown
): { start: Date; end: Date } | null {
	if (!wsData || typeof wsData !== 'object') {
		return null;
	}
	const inner = wsData as Record<string, unknown>;
	const series = inner['series'];
	if (!Array.isArray(series)) {
		return null;
	}
	let min: Date | null = null;
	let max: Date | null = null;
	for (const item of series) {
		if (!item || typeof item !== 'object') {
			continue;
		}
		const points = parseSeriesDataPoints(
			(item as { data?: unknown[] }).data
		);
		for (const point of points) {
			const d = seriesPointXToDate(point.x);
			if (!d) {
				continue;
			}
			if (!min || d < min) {
				min = d;
			}
			if (!max || d > max) {
				max = d;
			}
		}
	}
	if (!min || !max) {
		return null;
	}
	return { start: min, end: max };
}

/**
 * Whether WebSocket chart payload spans the expected global date range.
 * `null` when the payload has no inferable time series (donut/number) — caller may accept after refetch.
 */
export function webSocketCardDataCoversDateRange(
	wsData: unknown,
	expected: [string, string]
): boolean | null {
	const span = inferDateSpanFromChartWebSocketData(wsData);
	if (!span) {
		return null;
	}
	const expStart = parseISODate(expected[0]);
	const expEnd = parseISODate(expected[1]);
	const expectedDays = Math.max(
		1,
		Math.ceil((expEnd.getTime() - expStart.getTime()) / MS_PER_DAY)
	);
	const actualDays = Math.max(
		1,
		Math.ceil((span.end.getTime() - span.start.getTime()) / MS_PER_DAY)
	);

	if (
		span.start.getTime() >
		expStart.getTime() + GLOBAL_RANGE_START_SLACK_DAYS * MS_PER_DAY
	) {
		return false;
	}
	if (
		expectedDays > 60 &&
		actualDays <
			Math.min(
				expectedDays * 0.5,
				expectedDays - GLOBAL_RANGE_START_SLACK_DAYS
			)
	) {
		return false;
	}
	return true;
}
